import { UserDetailContext } from "./UserDetailContext";
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchUserDetailsService } from "../../global-services/user.service";
import { useContext } from "react";
import { UserContext } from "../user/UserContext";

function UserDetailProvider({ children }) {
    const { user, loggedIn } = useContext(UserContext);
    const [userDetail, setUserDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Use a ref to prevent unnecessary re-fetches if a request is already in progress
    const isFetching = useRef(false);

    /**
     * fetchUserDetail: High-level function to load/refresh user profile data.
     * Can be called manually by pages that need fresh data.
     */
    const fetchUserDetail = useCallback(async (role = user?.active_role) => {
        if (!loggedIn || !role || isFetching.current) return;
        
        isFetching.current = true;
        setIsLoading(true);
        setError(null);

        try {
            const data = await fetchUserDetailsService(role);
            setUserDetail(data);
        } catch (err) {
            setError(err);
            console.error('Failed to fetch user details:', err);
        } finally {
            setIsLoading(false);
            isFetching.current = false;
        }
    }, [loggedIn, user?.active_role]);

    /**
     * AUTO-LOAD: Only load if not already present.
     * This handles the initial load after login.
     */
    useEffect(() => {
        if (loggedIn && user?.active_role && !userDetail && !isFetching.current) {
            fetchUserDetail(user.active_role);
        } else if (!loggedIn) {
            setUserDetail(null);
        }
    }, [loggedIn, user?.active_role, userDetail, fetchUserDetail]);

    return (
        <UserDetailContext.Provider 
            value={{ 
                userDetail, 
                setUserDetail, 
                isDetailsLoading: isLoading, 
                detailsError: error,
                fetchUserDetail 
            }}
        >
            {children}
        </UserDetailContext.Provider>
    );
}

export default UserDetailProvider;